/**
 * Sidebar component (Preact port).
 *
 * Renders a DaisyUI drawer sidebar with brand header, action buttons, and
 * collapsible menu sections. Sections and entries are supplied as a model
 * (see createSidebarModel) so the app can add/update/delete entries reactively.
 */
import { useState } from 'preact/hooks';
import { Icon, EllipsisIcon } from './Icons.jsx';
import { showContextMenu } from './ContextMenu.jsx';

// A small reactive model used by the app to drive the sidebar.
export function createSidebarModel() {
  const state = {
    actions: [], // { id, label, icon, route, onClick, contextMenu }
    sections: [], // { id, label, icon, entries: [...] }
  };
  const listeners = new Set();
  const notify = () => listeners.forEach((fn) => fn());
  return {
    state,
    onChange: (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    addAction(action) {
      if (state.actions.some((a) => a.id === action.id)) return;
      state.actions.push(action);
      notify();
    },
    deleteAction(id) {
      state.actions = state.actions.filter((a) => a.id !== id);
      notify();
    },
    registerSection({ id, label, icon }) {
      if (!state.sections.some((s) => s.id === id)) {
        state.sections.push({ id, label, icon, entries: [] });
      } else {
        const s = state.sections.find((s) => s.id === id);
        s.label = label;
        s.icon = icon;
      }
      notify();
    },
    addEntry(sectionID, entry) {
      const s = state.sections.find((s) => s.id === sectionID);
      if (!s) return;
      if (s.entries.some((e) => e.id === entry.id)) return;
      s.entries.push(entry);
      notify();
    },
    updateEntry(sectionID, entryID, updates) {
      const s = state.sections.find((s) => s.id === sectionID);
      if (!s) return;
      const e = s.entries.find((e) => e.id === entryID);
      if (!e) return;
      Object.assign(e, updates);
      notify();
    },
    deleteEntry(sectionID, entryID) {
      const s = state.sections.find((s) => s.id === sectionID);
      if (!s) return;
      s.entries = s.entries.filter((e) => e.id !== entryID);
      notify();
    },
  };
}

// SidebarView renders the current model. It listens to the current hash to
// highlight the active route.
export function SidebarView({ model }) {
  const [version, setVersion] = useState(0);
  const [current, setCurrent] = useState(() => location.hash.replace(/^#/, '') || '/');

  useState(() => {
    const off = model.onChange(() => setVersion((v) => v + 1));
    const onHash = () => setCurrent(location.hash.replace(/^#/, '') || '/');
    window.addEventListener('hashchange', onHash);
    return () => {
      off();
      window.removeEventListener('hashchange', onHash);
    };
  });

  const navigate = (route) => {
    if (route == null) return;
    window.location.hash = route.startsWith('#') ? route : '#' + route;
    const toggle = document.getElementById('sidebar-toggle');
    if (toggle) toggle.checked = false;
  };

  const handleEntryClick = (entry) => {
    if (typeof entry.onClick === 'function') entry.onClick();
    else navigate(entry.route);
  };

  return (
    <div className="h-screen w-full bg-base-200 flex flex-col text-sm">
      <div className="flex items-center gap-3 px-4 h-14 shrink-0 border-b border-base-300">
        <span className="font-semibold text-base-content truncate">Talos</span>
        <label
          htmlFor="sidebar-toggle"
          className="btn btn-ghost btn-sm btn-square ml-auto lg:hidden"
          aria-label="close sidebar"
        >
          <svg viewBox="0 0 20 20" fill="currentColor" class="w-5 h-5" aria-hidden="true">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 0 1 1.414 0L10 8.586l4.293-4.293a1 1 0 1 1 1.414 1.414L11.414 10l4.293 4.293a1 1 0 0 1-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 0 1-1.414-1.414L8.586 10 4.293 5.707a1 1 0 0 1 0-1.414Z"
              clip-rule="evenodd"
            />
          </svg>
        </label>
      </div>

      {model.state.actions.length > 0 && (
        <div className="actions shrink-0 px-2 py-2 flex flex-col gap-1">
          {model.state.actions.map((action) => (
            <button
              key={action.id}
              type="button"
              data-action-id={action.id}
              className="btn btn-ghost btn-sm w-full justify-start gap-2 text-base-content/70 hover:text-base-content"
              onClick={() => {
                if (typeof action.onClick === 'function') action.onClick();
                else navigate(action.route);
              }}
              onContextMenu={(e) => {
                if (!action.contextMenu) return;
                e.preventDefault();
                e.stopPropagation();
                showContextMenu(action.contextMenu, e.clientX, e.clientY);
              }}
            >
              {action.icon && <Icon name={action.icon} className="w-4 h-4 shrink-0" />}
              <span className="flex-1 text-left">{action.label ?? action.id}</span>
            </button>
          ))}
        </div>
      )}

      <div className="flex-1 overflow-y-auto">
        <ul className="menu w-full bg-base-200 text-base-content">
          {model.state.sections.map((section) => (
            <li key={section.id}>
              <details open>
                <summary>
                  {section.icon && <Icon name={section.icon} className="w-4 h-4 shrink-0" />}
                  <span>{(section.label ?? section.id).toUpperCase()}</span>
                </summary>
                <ul>
                  {section.entries.map((entry) => {
                    const entryRoute = entry.route != null ? String(entry.route).replace(/^#/, '') : '';
                    const active = entryRoute && entryRoute === current;
                    return (
                      <li key={entry.id}>
                        <a
                          data-entry-id={entry.id}
                          className={`text-base-content/80 hover:text-base-content ${
                            active ? 'menu-active' : ''
                          }`}
                          href={entry.route || '#'}
                          onClick={(e) => {
                            e.preventDefault();
                            handleEntryClick(entry);
                          }}
                          onContextMenu={(e) => {
                            if (!entry.contextMenu) return;
                            e.preventDefault();
                            e.stopPropagation();
                            showContextMenu(entry.contextMenu, e.clientX, e.clientY);
                          }}
                        >
                          {entry.icon && <Icon name={entry.icon} className="w-4 h-4 shrink-0" />}
                          {entry.running && <span className="running-dot" />}
                          {entry.unread && !entry.running && <span className="unread-dot" />}
                          <span className="flex-1">{entry.label}</span>
                          {entry.contextMenu && <ContextMenuButton items={entry.contextMenu} />}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </details>
            </li>
          ))}
        </ul>
      </div>

      {/* Settings footer pinned to the bottom of the sidebar. */}
      <div className="shrink-0 px-2 py-2 border-t border-base-300">
        <button
          type="button"
          className={`btn btn-ghost btn-sm w-full justify-start gap-2 text-base-content/70 hover:text-base-content ${
            current === '/settings' ? 'menu-active' : ''
          }`}
          onClick={() => navigate('/settings')}
        >
          <Icon name="cog" className="w-4 h-4 shrink-0" />
          <span className="flex-1 text-left">Settings</span>
        </button>
      </div>
    </div>
  );
}

function ContextMenuButton({ items }) {
  return (
    <span
      className="context-menu-btn shrink-0 ml-auto grid place-items-center w-6 h-6 -mr-1 rounded text-base-content/50 hover:text-base-content hover:bg-base-200 transition-colors"
      role="button"
      tabIndex={0}
      aria-label="Open menu"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        const r = e.currentTarget.getBoundingClientRect();
        showContextMenu(items, r.left, r.bottom + 4);
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        const r = e.currentTarget.getBoundingClientRect();
        showContextMenu(items, r.left, r.bottom + 4);
      }}
    >
      <EllipsisIcon />
    </span>
  );
}